import { Box2, Vector2 } from '@seddi/math-v2';
import {
  BackSide,
  ClampToEdgeWrapping,
  FrontSide,
  Matrix3,
  MeshPhysicalMaterial,
  SRGBColorSpace,
  Texture,
  UVMapping,
} from '@seddi/three';

import type { StitchesTexturesMaterial } from '../resources/stitchesTexturesTypes';

const computeBoundingBox = (() => {
  const uv = new Vector2();

  return (uvs: Float32Array, target: Box2 = new Box2()) => {
    for (let i = 0; i < uvs.length; i += 2) {
      uv.set(uvs[i], uvs[i + 1]);
      target.expandByPoint(uv);
    }
    return target;
  };
})();

/**
 * Create stitches textures material.
 * @param desc - Material descriptor.
 */
export const create = (() => {
  const box = new Box2();
  const m0 = new Matrix3();
  const m1 = new Matrix3();
  const size = new Vector2();

  return (desc: StitchesTexturesMaterial, uvs: Float32Array): MeshPhysicalMaterial => {
    // textures transform
    box.makeEmpty();
    computeBoundingBox(uvs, box);
    box.sizeToRef(size);
    m0.makeTranslation(-box.min.x, -box.min.y);
    m1.makeScale(1.0 / size.x, 1.0 / size.y);
    m1.multiply(m0);
    // material
    const material = new MeshPhysicalMaterial();
    material.alphaTest = 0.001;
    material.polygonOffset = true;
    material.polygonOffsetFactor = 0;
    material.polygonOffsetUnits = -1;
    material.roughness = 0.9;
    material.transparent = true;
    // base color
    const map = new Texture(desc.baseColorTexture.image, UVMapping, ClampToEdgeWrapping, ClampToEdgeWrapping);
    map.colorSpace = SRGBColorSpace;
    map.matrix.copy(m1);
    map.matrixAutoUpdate = false;
    map.needsUpdate = true;
    material.map = map;
    // normal
    const normalMap = new Texture(
      desc.normalTexture.image,
      UVMapping,
      ClampToEdgeWrapping,
      ClampToEdgeWrapping,
    );
    normalMap.matrix.copy(m1);
    normalMap.matrixAutoUpdate = false;
    normalMap.needsUpdate = true;
    material.normalMap = normalMap;
    // side
    const isBack = desc.side === 'back';
    material.side = isBack ? BackSide : FrontSide;
    return material;
  };
})();
